/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import LoginFields from '@amblique/king-living/app/components/forms/login-fields';
import {Button, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import SocialLogin from '@amblique/king-living/app/components/social-login';

const StandardLogin = ({
  email,
  form,
  handleBackToLanding,
  handleForgotPasswordClick,
  hideEmail = false,
  idps = [],
  isSocialEnabled = false,
  setShowPasswordView,
  showBackToSignInOptions = false,
}) => {
  const styles = useMultiStyleConfig('FormAuth', {variant: 'login'});

  // Prefill email if provided from landing page
  useEffect(() => {
    if (email) {
      form.setValue('email', email);
    }
  }, [email, form]);

  const handleBack = () => {
    if (setShowPasswordView) {
      setShowPasswordView(false);
    } else if (handleBackToLanding) {
      handleBackToLanding();
    }
  };

  return (
    <Stack className="standard-login-stack" gap={0}>
      <LoginFields
        email={email}
        form={form}
        handleForgotPasswordClick={handleForgotPasswordClick}
        hideEmail={hideEmail}
        isEmailReadOnly={!!email}
      />
      <Stack className="button-container" {...styles.buttonContainer} {...styles.buttonContainerStandardLoginForm}>
        <Button
          isLoading={form.formState.isSubmitting}
          type="submit"
          onClick={() => {
            form.clearErrors('global');
          }}
          {...styles.buttonSubmit}
        >
          <FormattedMessage defaultMessage="Sign in" id="login_form.button.sign_in" />
        </Button>
        {isSocialEnabled && idps.length > 0 && (
          <>
            <Text {...styles.divider}>
              <FormattedMessage
                defaultMessage="Or continue with"
                id="login_page.or_continue_with"
              />
            </Text>
            <SocialLogin form={form} idps={idps} />
          </>
        )}
        {showBackToSignInOptions && (setShowPasswordView || handleBackToLanding) && (
          <Button
            variant="outlined-light"
            onClick={handleBack}
          >
            <FormattedMessage
              defaultMessage="Back to sign in options"
              id="login_form.button.back"
            />
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

StandardLogin.propTypes = {
  email: PropTypes.string,
  form: PropTypes.object,
  handleBackToLanding: PropTypes.func,
  handleForgotPasswordClick: PropTypes.func,
  hideEmail: PropTypes.bool,
  idps: PropTypes.arrayOf(PropTypes.string),
  isSocialEnabled: PropTypes.bool,
  setShowPasswordView: PropTypes.func,
  showBackToSignInOptions: PropTypes.bool,
};

export default StandardLogin;
